import { showUserAlert } from "../utils/userErrorMessage";
// src/pages/BusHubManage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Header from "../Component/Header";
import { supabase } from "../supabase/client";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { loadTossPayments } from "@tosspayments/tosspayments-sdk";

function Badge({ status }) {
  const base = "px-2 py-0.5 rounded text-xs font-semibold";
  const map = {
    applied: "bg-gray-100 text-gray-700",
    accepted: "bg-blue-100 text-blue-700",
    payment_held: "bg-amber-100 text-amber-700",
    in_progress: "bg-indigo-100 text-indigo-700",
    cancelled: "bg-red-100 text-red-700",
    settled: "bg-emerald-100 text-emerald-700",
  };
  return <span className={`${base} ${map[status] || "bg-gray-100 text-gray-600"}`}>{status}</span>;
}

function getStoredEmail() {
  return (
    localStorage.getItem("email") ||
    localStorage.getItem("userEmail") ||
    ""
  ).trim();
}

export default function BusHubManage() {
  const { id } = useParams(); // post id
  const navigate = useNavigate();

  const myRole = localStorage.getItem("role") || "";

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const [post, setPost] = useState(null);
  const [apps, setApps] = useState([]);
  const [holdAmount, setHoldAmount] = useState(""); // 결제 보류(에스크로) 금액 입력

  const [user, setUser] = useState(null);
  const [actorEmail, setActorEmail] = useState(getStoredEmail());
  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      setUser(user || null);
      setActorEmail((user?.email || getStoredEmail()).trim());
    });
  }, []);
  const isAuthor = useMemo(
    () => !!(post && ((user?.id && post.author_id === user.id) || post.author === actorEmail)),
    [actorEmail, post, user]
  );

  const fetchAll = async () => {
    setLoading(true);
    try {
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      const currentEmail = (currentUser?.email || getStoredEmail()).trim();
      if (!currentEmail) throw new Error("로그인 정보가 필요합니다.");
      setUser(currentUser || null);
      setActorEmail(currentEmail);

      // 게시글
      const { data: p, error: pErr } = await supabase
        .from("bus_hub_posts")
        .select("*")
        .eq("id", id)
        .single();
      if (pErr) throw pErr;
      setPost(p);
      setHoldAmount(String(p?.price ?? "")); // 기본은 글의 금액

      const author = (currentUser?.id && p.author_id === currentUser.id) || p.author === currentEmail;
      if (!author) {
        showUserAlert("게시글 작성자만 신청/결제 관리 화면에 접근할 수 있습니다.");
        navigate(`/bus-hub/${id}`);
        return;
      }

      // 신청 목록
      const { data: a, error: aErr } = await supabase
        .from("bus_hub_applications")
        .select("*")
        .eq("post_id", id)
        .order("created_at", { ascending: false });
      if (aErr) {
        console.warn("bus hub manage applications fetch failed:", aErr.message);
        setApps([]);
        return;
      }
      setApps(a || []);
    } catch (e) {
      console.error(e);
      showUserAlert("관리 화면을 불러오지 못했습니다.");
      navigate("/bus-hub");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!id) return;
    if (myRole !== "first" && myRole !== "operator") {
      showUserAlert("접근 권한이 없습니다.");
      navigate("/reservations");
      return;
    }
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // 공통 상태 변경 핸들러 (applications)
  const setAppStatus = async (appId, next, extra = {}) => {
    setBusy(true);
    try {
      const { error } = await supabase.rpc("bus_hub_update_application_status", {
        p_application_id: appId,
        p_status: next,
        p_extra: extra,
      });
      if (error) throw error;

      await fetchAll();
    } catch (e) {
      showUserAlert("상태 변경 실패: " + e.message);
    } finally {
      setBusy(false);
    }
  };

  // 1) 수락
  const handleAccept = async (app) => {
    if (!isAuthor) return;
    setBusy(true);
    try {
      const { error } = await supabase.rpc("bus_hub_accept_application", {
        p_application_id: app.id,
      });
      if (error) throw error;
      await fetchAll();
    } catch (e) {
      showUserAlert("수락 실패: " + e.message);
    } finally {
      setBusy(false);
    }
  };

  // 2) 결제 보류(에스크로) — 실제 PG 연동 전까지는 DB 기록만
  const handlePaymentHold = async (app) => {
    if (!isAuthor) return;
    const amountNum = Number(holdAmount);
    if (!Number.isFinite(amountNum) || amountNum < 100) {
      return showUserAlert("카드 결제 최소 금액은 100원입니다.");
    }

    // 결제 레코드(선택 스키마): bus_hub_payments(post_id, app_id, amount, status)
    setBusy(true);
    try {
      // 현재 로그인 사용자 (글 작성자)
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("로그인이 필요합니다.");

      // 토스 결제창 열기 (테스트 클라이언트키)
      const clientKey = process.env.REACT_APP_TOSS_CLIENT_KEY;
      const mkSafeOrderId = (raw) => raw.replace(/[^A-Za-z0-9_-]/g, "").slice(0, 64);

      // v2 필수: customerKey 전달
      const rawKey = (user.id || "guest").toString();
      const customerKey = rawKey.replace(/[^A-Za-z0-9_=.@-]/g, "").slice(0, 50);
      if (customerKey.length < 2) throw new Error("customerKey 생성 실패");

      const orderId = mkSafeOrderId(`bus_${id}_${app.id}_${Date.now()}`);
      const successUrl = `${window.location.origin}/toss/success?postId=${id}&appId=${app.id}&amount=${amountNum}`;
      const failUrl    = `${window.location.origin}/toss/fail?postId=${id}&appId=${app.id}`;
      const tossPayments = await loadTossPayments(clientKey);
      if (process.env.NODE_ENV !== "production") console.log("TossPayments 객체:", tossPayments);
      
      if (process.env.NODE_ENV !== "production") console.log(amountNum, orderId, successUrl, failUrl);
      const payment = tossPayments.payment({ customerKey }); // ✅ v2: 결제창 핸들러 생성
      await payment.requestPayment({          // ✅ 여기에 요청
        method: "CARD",
        amount: { value: amountNum, currency: "KRW" },
        orderId,
        orderName: `[에스크로보류] ${post?.title || "버스 모집"} - ${(app.requested_qty ?? 1)}대`,
        successUrl,
        failUrl,
        card: { flowMode: "DEFAULT", useEscrow: true },
      });
    } catch (e) {
      showUserAlert("결제 보류 실패: " + e.message);
      setBusy(false);
    }
  };

  // 3) 작업 시작(진행중)
  const handleStart = async (app) => {
    if (!isAuthor) return;
    await setAppStatus(app.id, "in_progress", { started_at: new Date().toISOString() });
  };

  // 4) 작업 취소
  const handleCancel = async (app) => {
    if (!isAuthor) return;
    if (!window.confirm("정말 취소하시겠습니까?")) return;

    setBusy(true);
    try {
      const needsPaymentCancel = ["payment_held", "in_progress"].includes(app.status);

      if (needsPaymentCancel) {
        const { data, error } = await supabase.functions.invoke("cancel-toss-payment", {
          body: { postId: id, applicationId: app.id, cancelReason: "버스 허브 작성자 취소" },
        });
        if (error || data?.ok === false) {
          throw new Error(data?.error || error?.message || "결제 취소 처리 실패");
        }
      } else {
        const { error } = await supabase.rpc("bus_hub_update_application_status", {
          p_application_id: app.id,
          p_status: "cancelled",
          p_extra: { cancelled_at: new Date().toISOString() },
        });
        if (error) throw error;
      }

      await fetchAll();
    } catch (e) {
      showUserAlert("취소 실패: " + e.message);
    } finally {
      setBusy(false);
    }
  };

  // 5) 정산(출금 완료)
  const handleSettle = async (app) => {
    if (!isAuthor) return;
    // 결제 테이블 상태 변경(held → settled)
    setBusy(true);
    try {
      const { error } = await supabase.rpc("bus_hub_settle_application", {
        p_application_id: app.id,
      });
      if (error) throw error;
      await fetchAll();
    } catch (e) {
      showUserAlert("정산 실패: " + e.message);
      setBusy(false);
    }
  };

  const handleApplyHoldAmount = async () => {
    const amountNum = Number(holdAmount);

    if (!Number.isFinite(amountNum) || amountNum < 100) {
      return showUserAlert("금액은 100원 이상이어야 합니다.");
    }

    setBusy(true);

    try {
      const { error } = await supabase.rpc("bus_hub_update_post_price", {
        p_post_id: id,
        p_price: amountNum,
      });
      if (error) throw error;

      showUserAlert(`${amountNum.toLocaleString()}원이 적용되었습니다.`);
      await fetchAll();
    } catch (e) {
      showUserAlert("금액 적용 실패: " + e.message);
    } finally {
      setBusy(false);
    }
  };



  if (loading || !post) {
    return (
      <div className="space-y-4">
        <Header />
        <div className="p-4">불러오는 중...</div>
      </div>
    );
  }

  const displayCompany = post.company_name || post.company || "-";

  return (
    <div className={`space-y-4 ${busy ? "pointer-events-none opacity-60" : ""}`}>
      <Header />
      <div className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">버스 허브 - 신청/결제 관리</h2>
          <div className="text-sm text-gray-600">글 상태: <Badge status={post.status || "open"} /></div>
        </div>

        <Card className="border-0 shadow-none">
          <CardContent className="bg-white space-y-2 p-4">
            <div className="font-semibold text-base">{post.title}</div>
            <div className="text-sm text-gray-600">
              작성 회사: {displayCompany} · 작성자: {post.author} · 지역: {post.region} · 차량 대수: {post.car_count}대 · 금액: {(post.price ?? 0).toLocaleString()}원
            </div>

            <div className="mt-3 flex items-center gap-2">
              <span className="text-sm">보류 금액(에스크로):</span>
              <Input
                value={holdAmount}
                onChange={(e) => setHoldAmount(e.target.value)}
                className="w-40"
              />
              <Button variant="secondary" onClick={handleApplyHoldAmount} disabled={busy}>
                금액 적용
              </Button>
              <Button variant="outline" onClick={() => navigate(`/bus-hub/${id}`)}>상세로</Button>
              <Button onClick={() => navigate("/bus-hub")} variant="outline">목록으로</Button>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-none">
          <CardContent className="bg-white p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="font-semibold">신청자 목록</div>
              <div className="text-sm text-gray-500">총 {apps.length}건</div>
            </div>

            <div className="overflow-x-auto">
              <table className="table-auto w-full border text-sm whitespace-nowrap">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="border p-2 text-left">신청일</th>
                    <th className="border p-2 text-left">신청자</th>
                    <th className="border p-2 text-right">요청대수</th>
                    <th className="border p-2 text-right">수락대수</th>
                    <th className="border p-2 text-left">상태</th>
                    <th className="border p-2 text-left">액션</th>
                    <th className="border p-2 text-right">보류금</th>
                  </tr>
                </thead>
                <tbody>
                  {apps.map((a) => (
                    <tr key={a.id}>
                      <td className="border p-2">{(a.created_at || "").replace("T", " ").slice(0, 16)}</td>
                      <td className="border p-2">{a.applicant_email}</td>
                      <td className="border p-2 text-right">{a.requested_qty ?? 1}대</td>
                      <td className="border p-2 text-right">{a.accepted_qty ?? 0}대</td>
                      <td className="border p-2"><Badge status={a.status} /></td>
                      <td className="border p-2">
                        <div className="flex flex-wrap gap-2">
                          <Button size="sm" onClick={() => handleAccept(a)} disabled={busy || a.status !== "applied"}>
                            수락
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handlePaymentHold(a)}
                            disabled={busy || (a.status !== "accepted")}
                            variant="secondary"
                          >
                            결제 보류(에스크로)
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleStart(a)}
                            disabled={busy || (a.status !== "payment_held")}
                            variant="secondary"
                          >
                            작업 시작
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleSettle(a)}
                            disabled={busy || (a.status !== "in_progress")}
                            className="bg-emerald-600 text-white hover:bg-emerald-700"
                          >
                            정산 완료
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleCancel(a)}
                            disabled={busy || a.status === "settled" || a.status === "cancelled"}
                            className="bg-red-600 text-white hover:bg-red-700"
                          >
                            취소
                          </Button>
                        </div>
                      </td>
                      <td className="border p-2 text-right">
                        {(a.hold_amount ?? 0).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                  {apps.length === 0 && (
                    <tr>
                      <td className="border p-4 text-center text-gray-500" colSpan={7}>
                        신청자가 없습니다.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <p className="text-xs text-gray-500 mt-3">
              * 결제/보류/환불/정산은 현재 DB 시뮬레이션입니다. 실제 PG 연동 시 payment intent/webhook으로 상태를 동기화하세요.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
